import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Smartphone, Info, Download } from "lucide-react";
import { toast } from "sonner";

/** Compacte "iPhone-tip"-broer voor Android/Chrome. Ondersteunt:
 *  - Native `beforeinstallprompt`-event (Chrome/Edge Android): één-klik installatie
 *  - Fallback: 3-staps SVG-uitleg voor menu → 'Toevoegen aan startscherm'
 */
export default function AndroidInstallHelp({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    setIsAndroid(typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || ""));
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const doInstall = async () => {
    if (!prompt) return;
    prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === "accepted") {
      toast.success("App wordt geïnstalleerd op je startscherm");
    } else {
      toast.info("Installatie geannuleerd — je kunt het later opnieuw proberen");
    }
    setPrompt(null);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:text-emerald-900 underline decoration-dotted underline-offset-2"
        data-testid="android-install-help-btn"
      >
        <Smartphone className="h-3 w-3" />
        {compact ? "Android-tip" : (isAndroid ? "Zo installeer je op Android" : "Op Android? Zo werkt het")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="android-install-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" /> Installeren op Android in 3 stappen
            </DialogTitle>
            <DialogDescription>
              Op Android open je de app via Chrome (of Edge/Samsung Internet) en installeer je hem als PWA. Daarna komen BKR-meldingen binnen als push-notificaties net als bij een normale app.
            </DialogDescription>
          </DialogHeader>

          {prompt && (
            <div className="p-3 rounded border border-emerald-300 bg-emerald-50 text-sm text-emerald-900 flex items-center justify-between gap-2 mb-2" data-testid="android-native-prompt">
              <div>
                <div className="font-semibold flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Direct installeren</div>
                <div className="text-xs">Je browser ondersteunt directe installatie — klik hier en klaar.</div>
              </div>
              <Button size="sm" onClick={doInstall} style={{ background: "var(--sage)" }} className="text-white" data-testid="android-native-install-btn">
                Installeer
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
            <AndroidStep n={1} title="Open in Chrome" body="Werkt met Chrome, Edge en Samsung Internet — niet met Firefox.">
              <AndroidMock>
                <BrowserChrome />
                <div style={{ position: "absolute", top: 4, right: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                  <div style={{ width: 3, height: 3, background: "#1f2937", borderRadius: "50%" }} />
                  <div style={{ width: 3, height: 3, background: "#1f2937", borderRadius: "50%" }} />
                  <div style={{ width: 3, height: 3, background: "#1f2937", borderRadius: "50%" }} />
                </div>
                <ArrowMark toX={90} toY={8} />
              </AndroidMock>
            </AndroidStep>

            <AndroidStep n={2} title="Menu → App installeren" body="Tik op de 3 puntjes rechtsboven. Zoek 'App installeren' of 'Toevoegen aan startscherm'.">
              <AndroidMock>
                <div style={{ position: "absolute", left: 6, right: 6, top: 20, borderRadius: 6, background: "#f9fafb", padding: 4 }}>
                  <MenuItem label="Nieuw tabblad" />
                  <MenuItem label="Bladwijzers" />
                  <MenuItem label="Downloads" />
                  <MenuItem label="App installeren" highlight />
                  <MenuItem label="Instellingen" />
                </div>
                <ArrowMark toX={50} toY={62} />
              </AndroidMock>
            </AndroidStep>

            <AndroidStep n={3} title="Bevestig & open" body="Tik op 'Installeren'. App verschijnt op startscherm — toestemming geven bij eerste push.">
              <AndroidMock>
                <div style={{ position: "absolute", left: 8, right: 8, top: 20, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} style={{
                      aspectRatio: "1/1",
                      background: i === 7 ? "var(--sage,#2D4A3E)" : "#e5e7eb",
                      borderRadius: 8,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "white", fontSize: 8, fontWeight: 700,
                    }}>
                      {i === 7 ? "W" : ""}
                    </div>
                  ))}
                </div>
                <ArrowMark toX={62} toY={45} />
              </AndroidMock>
            </AndroidStep>
          </div>

          <div className="mt-2 p-2 rounded border border-neutral-200 bg-neutral-50 text-[11px] text-neutral-600 flex items-start gap-2">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <div>
              <strong>Tip:</strong> zie je stap 2 geen 'App installeren'? Reload de pagina — Chrome heeft soms een paar seconden nodig om te herkennen dat dit een PWA is.
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setOpen(false)} data-testid="android-help-close" style={{ background: "var(--sage)" }} className="text-white">
              Begrepen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AndroidStep({ n, title, body, children }) {
  return (
    <div className="border border-neutral-200 rounded-lg p-2 bg-white">
      <div className="flex items-center gap-1.5 mb-1">
        <div className="w-5 h-5 rounded-full bg-[color:var(--sage)] text-white flex items-center justify-center text-[10px] font-bold">{n}</div>
        <div className="text-xs font-semibold text-neutral-800">{title}</div>
      </div>
      <div className="flex justify-center py-1">{children}</div>
      <div className="text-[10px] text-neutral-500 leading-snug">{body}</div>
    </div>
  );
}

function AndroidMock({ children }) {
  return (
    <div style={{
      position: "relative", width: 90, height: 140,
      background: "#374151", borderRadius: 10, padding: 3,
    }}>
      <div style={{ position: "absolute", inset: 6, background: "white", borderRadius: 6, overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

function BrowserChrome() {
  return (
    <>
      <div style={{ height: 14, background: "#f3f4f6", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", padding: "0 4px" }}>
        <div style={{ height: 8, flex: 1, background: "white", borderRadius: 4, marginRight: 12 }} />
      </div>
      <div style={{ padding: 6, fontSize: 8, color: "#6b7280" }}>
        <div style={{ height: 4, width: "60%", background: "#e5e7eb", borderRadius: 1, marginBottom: 3 }} />
        <div style={{ height: 4, width: "80%", background: "#e5e7eb", borderRadius: 1, marginBottom: 3 }} />
        <div style={{ height: 4, width: "45%", background: "#e5e7eb", borderRadius: 1 }} />
      </div>
    </>
  );
}

function MenuItem({ label, highlight }) {
  return (
    <div style={{
      padding: "2px 3px",
      background: highlight ? "#c7d2fe" : "transparent",
      borderRadius: 2,
      fontSize: 6,
      color: highlight ? "#312e81" : "#374151",
      fontWeight: highlight ? 700 : 400,
      marginBottom: 1,
    }}>{label}</div>
  );
}

function ArrowMark({ toX, toY }) {
  return (
    <div style={{
      position: "absolute", left: `${toX - 8}%`, top: `${toY - 8}%`, width: 16, height: 16,
      borderRadius: "50%", background: "rgba(220,38,38,0.35)", border: "2px solid #dc2626",
      animation: "pulse 1.5s ease-in-out infinite",
    }} />
  );
}
